import Keycloak, { type KeycloakConfig } from 'keycloak-js';

import { i18n } from '@/lib/i18n';

const keycloakConfig: KeycloakConfig = {
    url: import.meta.env.VITE_KEYCLOAK_URL ?? 'http://keycloak.localhost:8080',
    realm: import.meta.env.VITE_KEYCLOAK_REALM ?? 'invisible',
    clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID ?? 'invisible-frontend'
};

export const keycloak = new Keycloak(keycloakConfig);

let authRedirectInProgress = false;
const authRedirectListeners = new Set<() => void>();

function setAuthRedirectInProgress(next: boolean) {
    if (authRedirectInProgress === next) {
        return;
    }

    authRedirectInProgress = next;
    authRedirectListeners.forEach((listener) => listener());
}

function hasAuthCallbackParams(): boolean {
    if (typeof window === 'undefined') {
        return false;
    }

    const params = new URLSearchParams(window.location.search);
    const hasAuthorizationCodeFlowParams = params.has('code') && params.has('state');

    return hasAuthorizationCodeFlowParams || params.has('session_state') || params.has('error');
}

/**
 * Refreshes the Keycloak access token when the session is authenticated.
 */
export async function ensureFreshToken(minValidity = 30): Promise<string | null> {
    if (!keycloak.authenticated) {
        return null;
    }

    try {
        await keycloak.updateToken(minValidity);
        return keycloak.token ?? null;
    } catch {
        return null;
    }
}

/**
 * Returns the current raw access token.
 */
export function getAccessToken(): string | null {
    return keycloak.token ?? null;
}

/**
 * Returns the most user-friendly display name available from the token.
 */
export function getUserDisplayName(): string {
    const parsed = keycloak.tokenParsed;
    return parsed?.name ?? parsed?.preferred_username ?? i18n.t('common.guest');
}

/**
 * Returns the authenticated user's email when present.
 */
export function getUserEmail(): string | null {
    const email = keycloak.tokenParsed?.email;
    return typeof email === 'string' ? email : null;
}

/**
 * Returns the authenticated user's stable unique identifier.
 */
export function getUserId(): string | null {
    const subject = keycloak.tokenParsed?.sub;
    return typeof subject === 'string' ? subject : null;
}

export function isAuthRedirectInProgress(): boolean {
    return authRedirectInProgress;
}

export function subscribeToAuthRedirect(listener: () => void): () => void {
    authRedirectListeners.add(listener);

    return () => {
        authRedirectListeners.delete(listener);
    };
}

export function resetAuthRedirectStateForTests() {
    setAuthRedirectInProgress(false);
}

export async function runAuthRedirect(redirect: () => Promise<void>): Promise<void> {
    if (typeof window === 'undefined' || authRedirectInProgress || hasAuthCallbackParams()) {
        return;
    }

    setAuthRedirectInProgress(true);

    try {
        await redirect();
    } catch (error) {
        setAuthRedirectInProgress(false);
        throw error;
    }
}

/**
 * Redirects the user to Keycloak login and returns them to the current page.
 */
export async function redirectToLogin(): Promise<void> {
    await runAuthRedirect(() => keycloak.login({ redirectUri: window.location.href }));
}
